import { useMemo, useState } from "react";
import { useListProjectFiles, type ProjectFileMeta } from "@vorloop/api-client-react";

interface TreeNode {
  readonly name: string;
  readonly path: string;
  readonly isDir: boolean;
  readonly children: TreeNode[];
  readonly size?: number;
  readonly modifiedAt?: string;
}

function buildTree(files: ReadonlyArray<ProjectFileMeta>): TreeNode {
  const root: TreeNode = { name: "", path: "", isDir: true, children: [] };
  const dirs = new Map<string, TreeNode>([["", root]]);

  for (const file of files) {
    const parts = file.path.split("/");
    let parent = root;
    let acc = "";
    for (let i = 0; i < parts.length; i += 1) {
      const isLast = i === parts.length - 1;
      const part = parts[i]!;
      const childPath = acc ? `${acc}/${part}` : part;

      if (isLast) {
        parent.children.push({
          name: part,
          path: childPath,
          isDir: false,
          children: [],
          size: file.size,
          modifiedAt: file.modifiedAt,
        });
      } else {
        let dir = dirs.get(childPath);
        if (!dir) {
          dir = { name: part, path: childPath, isDir: true, children: [] };
          dirs.set(childPath, dir);
          parent.children.push(dir);
        }
        parent = dir;
      }
      acc = childPath;
    }
  }

  sortTree(root);
  return root;
}

function sortTree(node: TreeNode) {
  node.children.sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  for (const c of node.children) if (c.isDir) sortTree(c);
}

export interface FileTreeProps {
  readonly projectId: string;
  readonly selectedPath: string | null;
  readonly onSelect: (path: string) => void;
}

export function FileTree({ projectId, selectedPath, onSelect }: FileTreeProps) {
  const { data, isLoading, error, refetch } = useListProjectFiles(projectId);
  const tree = useMemo(() => buildTree(data?.items ?? []), [data]);

  return (
    <div
      style={{
        height: "100%",
        background: "var(--bg-panel)",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid var(--border-default)",
      }}
    >
      <header
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--border-default)",
          fontSize: 12,
          color: "var(--text-secondary)",
          fontWeight: 600,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>Files</span>
        <button
          type="button"
          onClick={() => void refetch()}
          title="Refresh"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--text-tertiary)",
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          ↻
        </button>
      </header>

      <div style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
        {isLoading ? (
          <div style={{ padding: 12, color: "var(--text-tertiary)", fontSize: 12 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 12, color: "var(--accent-red)", fontSize: 12 }}>
            {error.message}
          </div>
        ) : tree.children.length === 0 ? (
          <div style={{ padding: 12, color: "var(--text-tertiary)", fontSize: 12 }}>
            No files yet. Send a prompt to the agent.
          </div>
        ) : (
          tree.children.map((c) => (
            <TreeRow
              key={c.path}
              node={c}
              depth={0}
              selectedPath={selectedPath}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TreeRow({
  node,
  depth,
  selectedPath,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
}) {
  const [open, setOpen] = useState(depth < 1);

  if (node.isDir) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            width: "100%",
            textAlign: "left",
            background: "transparent",
            border: "none",
            color: "var(--text-secondary)",
            cursor: "pointer",
            padding: "3px 8px",
            paddingLeft: 8 + depth * 12,
            fontSize: 12,
            fontFamily: "var(--font-sans)",
          }}
        >
          <span style={{ color: "var(--text-tertiary)", marginRight: 4 }}>
            {open ? "▾" : "▸"}
          </span>
          {node.name}
        </button>
        {open &&
          node.children.map((c) => (
            <TreeRow
              key={c.path}
              node={c}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
            />
          ))}
      </div>
    );
  }

  const isSelected = selectedPath === node.path;
  return (
    <button
      type="button"
      onClick={() => onSelect(node.path)}
      style={{
        width: "100%",
        textAlign: "left",
        background: isSelected ? "var(--bg-active)" : "transparent",
        border: "none",
        color: isSelected ? "var(--text-primary)" : "var(--text-secondary)",
        cursor: "pointer",
        padding: "3px 8px",
        paddingLeft: 8 + (depth + 1) * 12,
        fontSize: 12,
        fontFamily: "var(--font-sans)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {node.name}
    </button>
  );
}
